import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, CreditCard, Crown, Loader2 } from "lucide-react";
import { api } from "../api";
import { useMember } from "../MemberContext";
import { H1, Overline, PageLoader, TIER_LABEL } from "../ui";

const RANK = { free: 0, premium: 1, premium_plus: 2 };
const eur = (n) => `${Number(n).toFixed(2).replace(".", ",")} €`;

function Buy({ p, interval, onClose }) {
  const [code, setCode] = useState("");
  const [check, setCheck] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setCheck(null); }, [interval]);
  const price = p.kind === "membership" ? p[`price_${interval}`] : p.price;
  const final = check ? check.final_price : price;
  const testCoupon = async () => {
    try { setCheck(await api("/shop/coupon/check", { method: "POST", body: { code, product_id: p.product_id, interval } })); } catch (e) { setCheck(null); toast.error(e.message); }
  };
  const pay = async (method) => {
    setBusy(true);
    try {
      const d = await api("/shop/checkout", { method: "POST", body: { product_id: p.product_id, interval, coupon: check ? code : null, method, origin_url: window.location.origin } });
      if (d.granted) { toast.success("Freigeschaltet!"); window.location.href = "/training/start"; }
      else if (d.checkout_url) window.location.href = d.checkout_url;
      else if (d.paypal_url) { window.open(d.paypal_url, "_blank"); toast.success(`Bitte bei PayPal als Nachricht angeben: ${d.reference}`, { duration: 15000 }); onClose(d.reference); }
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  return (
    <div className="border border-[#c1ff72] bg-[#c1ff72]/[0.04] p-4 sm:p-6 mt-4 space-y-4 min-w-0" data-testid={`buy-box-${p.product_id}`}>
      <p className="text-white">Preis: <b data-testid="buy-final-price">{eur(final)}</b>{p.kind === "membership" && (interval === "month" ? " / Monat" : " / Jahr")}{check && final !== price && <span className="text-[#71717A] line-through ml-2">{eur(price)}</span>}</p>
      <div className="flex gap-2"><input value={code} onChange={(e) => { setCode(e.target.value.toUpperCase()); setCheck(null); }} placeholder="Gutscheincode" className="h-11 px-3 bg-[#0A0A0A] border border-white/15 text-white text-sm flex-1 min-w-0" data-testid="coupon-input" />
        <button onClick={testCoupon} disabled={!code} className="h-11 px-4 border border-white/20 text-sm text-white shrink-0" data-testid="coupon-apply">Einlösen</button></div>
      {check && <p className="text-xs text-[#c1ff72]" data-testid="coupon-ok">Gutschein „{check.label}“ angewendet{check.free_months ? ` – ${check.free_months} Monat(e) gratis` : ""}.</p>}
      <div className="flex flex-col gap-2">
        <button disabled={busy} onClick={() => pay("stripe")} className="min-h-12 py-2 px-3 bg-[#c1ff72] text-black font-bold flex items-center justify-center gap-2 text-center" data-testid="pay-stripe">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}{final <= 0 ? "Gratis freischalten" : "Karte oder SEPA-Lastschrift"}</button>
        {final > 0 && <button disabled={busy} onClick={() => pay("paypal")} className="h-12 border border-white/20 text-white font-semibold" data-testid="pay-paypal">PayPal</button>}
      </div>
      <p className="text-xs text-[#71717A]">Karte/SEPA: sichere Zahlung über Stripe, Freischaltung automatisch. PayPal: Freischaltung, sobald der Trainer den Eingang bestätigt hat. {p.kind === "membership" && "Abos verlängern sich automatisch und sind jederzeit zum Laufzeitende kündbar."}</p>
    </div>
  );
}

function MyStatus({ member }) {
  const [d, setD] = useState(null);
  const load = () => api("/shop/me").then(setD).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!d) return null;
  const cancel = async () => { if (!window.confirm("Abo wirklich kündigen?")) return; try { toast.success((await api("/shop/cancel-subscription", { method: "POST" })).message); load(); } catch (e) { toast.error(e.message); } };
  const pending = d.orders.filter((o) => o.status === "pending");
  return (
    <div className="bg-[#121212] border border-white/10 p-6 mb-10" data-testid="membership-status">
      <p className="text-sm text-[#A1A1AA]">Aktuell: <b className="text-white">{TIER_LABEL[member.tier]}</b>{member.tier_until && member.tier !== "free" && ` · gültig bis ${new Date(member.tier_until).toLocaleDateString("de-DE")}`}</p>
      {d.has_subscription && !d.subscription_cancelled && <button onClick={cancel} className="text-xs text-red-300 underline mt-2" data-testid="subscription-cancel">Abo kündigen</button>}
      {d.subscription_cancelled && <p className="text-xs text-[#71717A] mt-2">Abo gekündigt – läuft zum Ende des Zeitraums aus.</p>}
      {pending.map((o) => <p key={o.order_id} className="text-xs text-yellow-200 mt-2" data-testid={`pending-order-${o.order_id}`}>PayPal-Zahlung {o.order_id} ({eur(o.amount)}) wartet auf Bestätigung.</p>)}
      {d.purchases.length > 0 && <p className="text-xs text-[#A1A1AA] mt-2">Gekauft: {d.purchases.map((x) => x.title).join(", ")}</p>}
    </div>
  );
}

export default function Membership() {
  const { member } = useMember();
  const [items, setItems] = useState(null);
  const [interval, setInterval] = useState("month");
  const [open, setOpen] = useState(null);
  const [p] = useSearchParams();
  useEffect(() => { api("/shop/products").then((d) => setItems(d.items)); if (p.get("abgebrochen")) toast("Zahlung abgebrochen – es wurde nichts berechnet."); }, [p]);
  if (!items) return <PageLoader />;
  const tiers = items.filter((x) => x.kind === "membership");
  const extras = items.filter((x) => x.kind !== "membership");
  return (
    <div data-testid="membership-page">
      <Overline className="mb-3">Mitgliedschaft</Overline>
      <H1 className="mb-8 !text-5xl sm:!text-6xl">Mehr Training. Mehr Fortschritt.</H1>
      <MyStatus member={member} />
      <div className="flex gap-1 mb-6" data-testid="interval-toggle">{[["month", "Monatlich"], ["year", "Jährlich"]].map(([id, l]) => <button key={id} onClick={() => setInterval(id)} className={`px-5 py-2 text-sm border ${interval === id ? "bg-white text-black border-white" : "border-white/20 text-white"}`} data-testid={`interval-${id}`}>{l}</button>)}</div>
      <div className="grid md:grid-cols-2 gap-4">{tiers.map((t) => (
        <div key={t.product_id} className={`p-5 sm:p-8 min-w-0 border ${t.tier === "premium_plus" ? "border-[#FFD700]/50" : "border-[#c1ff72]/50"} bg-[#121212]`} data-testid={`tier-${t.product_id}`}>
          <p className="flex items-center gap-2 font-['Bebas_Neue'] text-4xl text-white"><Crown className={`w-6 h-6 ${t.tier === "premium_plus" ? "text-[#FFD700]" : "text-[#c1ff72]"}`} />{t.title}</p>
          <p className="font-['Bebas_Neue'] text-5xl text-white mt-4">{eur(t[`price_${interval}`])}<span className="text-lg text-[#71717A]"> / {interval === "month" ? "Monat" : "Jahr"}</span></p>
          <p className="text-sm text-[#A1A1AA] mt-4 flex gap-2"><Check className="w-4 h-4 text-[#c1ff72] shrink-0 mt-0.5" />{t.description}</p>
          {member.tier === t.tier ? <p className="mt-6 text-[#c1ff72] text-sm" data-testid={`tier-active-${t.product_id}`}>Dein aktueller Tarif</p>
            : (RANK[member.tier] || 0) > RANK[t.tier] ? <p className="mt-6 text-[#71717A] text-sm" data-testid={`tier-included-${t.product_id}`}>In deinem Tarif enthalten</p>
            : open === t.product_id ? <Buy p={t} interval={interval} onClose={() => setOpen(null)} /> : <button onClick={() => setOpen(t.product_id)} className="mt-6 h-12 px-8 bg-[#c1ff72] text-black font-bold uppercase tracking-wider" data-testid={`tier-choose-${t.product_id}`}>Auswählen</button>}
        </div>
      ))}</div>
      {extras.length > 0 && <><p className="font-['Bebas_Neue'] text-3xl text-white mt-14 mb-4">Programme & Live-Tickets</p>
        <div className="grid md:grid-cols-2 gap-4">{extras.map((x) => (
          <div key={x.product_id} className="p-6 border border-white/10 bg-[#121212]" data-testid={`product-${x.product_id}`}>
            <p className="text-white font-semibold">{x.title}</p><p className="text-sm text-[#A1A1AA] mt-1">{x.description}</p><p className="text-white mt-3">{eur(x.price)}</p>
            {open === x.product_id ? <Buy p={x} interval="once" onClose={() => setOpen(null)} /> : <button onClick={() => setOpen(x.product_id)} className="mt-4 h-10 px-5 border border-[#c1ff72] text-[#c1ff72] text-sm" data-testid={`product-buy-${x.product_id}`}>Kaufen</button>}
          </div>
        ))}</div></>}
    </div>
  );
}

export function PaymentResult() {
  const [p] = useSearchParams();
  const { reload } = useMember();
  const [st, setSt] = useState({ status: "checking" });
  useEffect(() => {
    const sid = p.get("session_id");
    let n = 0; let stop = false;
    const poll = async () => {
      try {
        const d = await api(`/shop/status/${sid}`);
        setSt(d);
        if (d.payment_status === "paid") { reload(); return; }
        if (["failed", "expired"].includes(d.payment_status) || d.status === "processing") return;
      } catch (e) { setSt({ status: "error", error: e.message }); return; }
      if (!stop && ++n < 15) setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [p, reload]);
  const msg = st.payment_status === "paid" ? `Zahlung erfolgreich – ${st.title} ist freigeschaltet.` : st.status === "processing" ? "Die Lastschrift wird bearbeitet. Wir schalten frei, sobald sie bestätigt ist (meist 1–3 Werktage)." : ["failed", "expired"].includes(st.payment_status) ? "Die Zahlung ist fehlgeschlagen oder abgelaufen. Es wurde nichts freigeschaltet." : st.error || "Zahlung wird geprüft …";
  return (
    <div className="max-w-xl py-16" data-testid="payment-result">
      <Overline className="mb-3">Zahlung</Overline>
      <p className="text-xl text-white" data-testid="payment-result-text">{msg}</p>
      <Link to="/training/start" className="inline-block mt-8 text-[#c1ff72] underline">Zum Training</Link>
    </div>
  );
}
